import React from "react";
import {
  BaseDocumentNode,
  ArrayValueNode,
  ComponentNode,
  FunctionValueNode,
  HTMLNode,
  NullishValueNode,
  ObjectValueNode,
  PrimitiveValueNode,
  DocumentNode,
  Node,
  parseNode,
} from "../ast";
import { createContextManager, defined, UnreachableError } from "../shared";
import { Dependency, Import, Declaration, getTracker, isTrackedObject } from "../tagging";
import { importRegistry } from "./import-registry";

function* chain<T>(generators: Iterable<Generator<T>>): Generator<T> {
  for (const item of generators) {
    yield* item;
  }
}

function resolveReference(value: unknown): string {
  return withDependencyTracking((state) => {
    if (importRegistry.isRegistered(value)) {
      value = importRegistry.resolve(value);
    }
    if (isTrackedObject(value)) {
      const tracker = getTracker(value);
      let name: string;
      if (tracker.target instanceof Import || tracker.target instanceof Declaration) {
        defined(state?.dependencies, "rendered without tracking context").add(tracker.target);
        name = tracker.target.name;
      } else {
        name = resolveReference(tracker.target.value);
      }
      return `${name}${tracker.accesses
        .map((access) => {
          if (access.type === "apply") {
            return `(${access.args.map((value) => Array.from(renderToValueTokens(value)).join("")).join(", ")})`;
          }
          if (isNaN(Number(access.field)) || isNaN(parseFloat(access.field))) {
            return `.${access.field}`;
          } else {
            // means access.field is numerical
            return `[${access.field}]`;
          }
        })
        .join("")}`;
    } else {
      console.error(
        "Unable to resolve reference, ensure the value is tracked and registered.",
        value,
      );
      throw new Error(
        "Unable to resolve reference, check the console message for more information",
      );
    }
  });
}

function* renderToValueTokens(node: Node): Generator<string> {
  if (node instanceof FunctionValueNode) {
    yield resolveReference(node.call);
  } else if (node instanceof PrimitiveValueNode) {
    if (typeof node.value === "string") {
      yield `"${node.value}"`;
    } else {
      yield `${node.value}`;
    }
  } else if (node instanceof ArrayValueNode) {
    yield `[`;
    for (const item of node.items) {
      yield* renderToValueTokens(item);
      yield `,`;
    }
    yield `]`;
  } else if (node instanceof ObjectValueNode) {
    yield `{`;
    for (const [k, v] of node.entries) {
      yield `${k}:`;
      yield* renderToValueTokens(v);
      yield `,`;
    }
    yield `}`;
  } else if (node instanceof BaseDocumentNode) {
    yield* renderToJsxTokens(node);
  } else if (node instanceof NullishValueNode) {
    yield `undefined`;
  } else {
    throw new UnreachableError(node);
  }
}

function* renderToPropsTokens(props: ObjectValueNode): Generator<string> {
  for (const [key, value] of props.entries) {
    if (value instanceof PrimitiveValueNode && typeof value.value === "string") {
      yield ` ${key}=`;
      if (key === "className") {
        yield `"${value.value.replace(/\s\s+/g, " ").trim()}"`;
      } else {
        yield* renderToValueTokens(value);
      }
    } else {
      yield ` ${key}={`;
      yield* renderToValueTokens(value);
      yield `}`;
    }
  }
}

function* renderToJsxTokens(node: Node): Generator<string> {
  if (node instanceof PrimitiveValueNode) {
    if (typeof node.value === "string") {
      yield `${node.value}`;
    } else if (typeof node.value === "number") {
      yield `{${node.value}}`;
    } else if (typeof node.value === "boolean") {
      yield "";
    }
  } else if (node instanceof NullishValueNode) {
    yield "";
  } else if (node instanceof ArrayValueNode) {
    yield* chain(node.items.map(renderToJsxTokens));
  } else if (node instanceof ObjectValueNode) {
    throw new Error("Objects are not valid as a React child");
  } else if (node instanceof FunctionValueNode) {
    if (isTrackedObject(node.call)) {
      return resolveReference(node.call);
    } else {
      throw new Error("Functions are not valid as a React child");
    }
  } else if (node instanceof HTMLNode || node instanceof ComponentNode) {
    const tagName = node instanceof HTMLNode ? node.tagName : resolveReference(node.Component);
    const children = node.children;
    if (children.length > 0) {
      yield `<${tagName}`;
      yield* renderToPropsTokens(
        node.props.transform((props) => props.filter(([k]) => k !== "children")),
      );
      yield `>`;
      yield* chain(children.map(renderToJsxTokens));
      yield `</${tagName}>`;
    } else {
      yield `<${tagName}`;
      yield* renderToPropsTokens(
        node.props.transform((props) => props.filter(([k]) => k !== "children")),
      );
      yield `/>`;
    }
  } else {
    throw new UnreachableError(node);
  }
}

function renderNodeToString(node: DocumentNode): string {
  return Array.from(renderToJsxTokens(node)).join("");
}

const withDependencyTracking = createContextManager<
  | {
      dependencies: Set<Dependency>;
    }
  | undefined
>(undefined);

function* renderImportsToTokens(imports: Import[]) {
  const nameImportMap: Map<string, Set<Import>> = new Map();
  const defaultImportMap: Map<string, Import> = new Map();
  const namespaceImportMap: Map<string, Import> = new Map();
  const allPaths = new Set<string>();
  imports.forEach((dependency) => {
    allPaths.add(dependency.path);
    if (dependency.form === "named") {
      if (!nameImportMap.has(dependency.path)) {
        nameImportMap.set(dependency.path, new Set());
      }
      nameImportMap.get(dependency.path)!.add(dependency);
    } else if (dependency.form === "default") {
      defaultImportMap.set(dependency.path, dependency);
    } else if (dependency.form === "namespace") {
      namespaceImportMap.set(dependency.path, dependency);
    }
  });
  for (const path of Array.from(allPaths).sort((a, b) => a.localeCompare(b))) {
    if (namespaceImportMap.has(path)) {
      yield `import * as ${namespaceImportMap.get(path)!.name} from "${path}";`;
    }

    const sections: string[] = [];
    if (defaultImportMap.has(path)) {
      sections.push(`${defaultImportMap.get(path)!.name}`);
    }
    if (nameImportMap.has(path)) {
      sections.push(
        `{ ${Array.from(nameImportMap.get(path)!)
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((imported) => imported.name)
          .join(", ")} }`,
      );
    }
    if (sections.length > 0) {
      yield `import ${sections.join(", ")} from "${path}";`;
    }
  }
}

export function renderToSource(node: DocumentNode, name = "Section"): string {
  return withDependencyTracking((_, setState) => {
    const dependencies = new Set<Dependency>([new Import("React", React, "default", "react")]);
    setState({ dependencies });
    const jsx = renderNodeToString(node);
    const imports: Import[] = [];
    const statics: string[] = [];
    const dynamics: string[] = [];
    while (dependencies.size > 0) {
      const dependency = defined(dependencies.values().next().value);
      dependencies.delete(dependency);
      if (dependency.type === "declaration") {
        dependency.dependencies.forEach(resolveReference);
        if (dependency.scope === "static") {
          if (dependency.src?.js != null) {
            statics.push(`const ${dependency.name} =` + dependency.src.js) + ";";
          } else {
            statics.push(
              `const ${dependency.name} =` +
                Array.from(renderToValueTokens(parseNode(dependency.value))).join("") +
                ";",
            );
          }
        } else {
          if (dependency.src?.js != null) {
            dynamics.push(`const ${dependency.name} = ` + dependency.src.js) + ";";
          } else {
            dynamics.push(
              `const ${dependency.name} = ` +
                Array.from(renderToValueTokens(parseNode(dependency.value))).join("") +
                ";",
            );
          }
        }
      } else {
        imports.push(dependency);
      }
    }
    const importSection = Array.from(renderImportsToTokens(imports)).join("\n");
    return `"use client"\n\n${importSection}\n\n${statics.join("\n\n")}\n\nexport function ${name}() {\n${dynamics.reverse().join("\n")}return (${jsx});\n}`;
  });
}
